import { FormikErrors, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import {
  AccountNumber,
  AccountType,
  Country,
  DefaultAccWithType,
} from '@/core/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@/hooks';
import { getChangelogs, postClientAccountNumbers } from '@/core/api';
import {
  activeCompanySelector,
  queryKeySelector,
} from '@/core/store/user/userSlice';
import { useCOA } from './Seller/useCOA';
import PageLayout from '@/wrappers/PageLayout';
import { COATabHeader } from '@/components/COATabHeader';
import { COACell } from '@/components/COACell';
import {
  isCustom,
  REVENUE_ACCS_TYPES,
} from '@/components/ChartOfAccounts/ChartOfAccounts.helpers';
import useNotification from '@/hooks/useNotification';
import { COARevenueAccountsInfoBlock } from '@/components/COARevenueAccountsInfoBlock';
import { useTranslation } from 'react-i18next';
import { COALastUpdated } from '@/components/ChartOfAccounts/COALastUpdated';
import { Loader } from '@/components/Loader/Loader';

interface FormValues {
  vat_rate_high: string;
  vat_rate_low: string;
  rest: string;
  b2b_buyer_vat_number_not_null: string;
  arrival_country_not_EU: string;
  commingling_buy_taxable: string;
}

export const COARevenue = () => {
  const { t } = useTranslation();
  const querClient = useQueryClient();
  const { showNotification } = useNotification();
  const queryKey = useAppSelector(queryKeySelector);
  const activeCompany = useAppSelector(activeCompanySelector);

  const [isEditing, setIsEditing] = useState(false);
  const [initialState, setInitialState] = useState<FormValues>(
    {} as FormValues
  );
  const [accountsMap, setAccountsMap] = useState<Record<string, AccountNumber>>(
    {}
  );

  const {
    defaultAccountNumbers,
    clientAccountNumbers,
    accountTypes,
    countries,
  } = useCOA();

  const revenueAccsIds = clientAccountNumbers
    ?.filter(
      (a) =>
        REVENUE_ACCS_TYPES.includes(a.account_type.account_type) &&
        (a.account_type.account_type === 'rest' ||
          a.country?.short_name === 'DE')
    )
    .map((a) => a.id);

  const {
    data: revenueAccsChangeLogs,
    isLoading: isLoadingRevenueAccsChangeLogs,
  } = useQuery({
    queryKey: ['RevenueAccsChangeLogs', queryKey],
    queryFn: () =>
      getChangelogs(
        activeCompany?.company_id || '',
        'account_number',
        revenueAccsIds
      ),
    enabled: !!activeCompany?.company_id && !!revenueAccsIds?.length,
  });

  const countriesMap = useMemo(
    () =>
      (countries || []).reduce((acc: Record<string, Country>, c) => {
        acc[c.short_name] = c;
        return acc;
      }, {}),
    [countries]
  );

  const accountTypesMap = useMemo(() => {
    return (accountTypes || []).reduce(
      (acc: Record<string, AccountType>, item: AccountType) => {
        acc[item.account_type] = item;
        return acc;
      },
      {}
    );
  }, [accountTypes]);

  const formik = useFormik<FormValues>({
    initialValues: {
      vat_rate_high: '',
      vat_rate_low: '',
      rest: '',
      b2b_buyer_vat_number_not_null: '',
      arrival_country_not_EU: '',
      commingling_buy_taxable: '',
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      Object.entries(values).forEach(([k, v]) => {
        if (!v) {
          errors[k as keyof FormValues] = 'Required';
        } else if (v.length < 4 || v.length > 7) {
          errors[k as keyof FormValues] = 'Between 4—7 digits';
        } else if (+v <= 0) {
          errors[k as keyof FormValues] = 'Account number invalid';
        }
      });
      return errors;
    },
    onSubmit: async (values) => {
      if (!activeCompany?.company_id || !filteredDefaultAccounts) return;

      const accountNumbers = Object.entries(values).map(
        ([k, value]: [k: string, value: string]) => {
          const id = accountsMap[k]?.id;
          if (
            !id &&
            filteredDefaultAccounts.some(
              (a) => a.account_type === k && a.account_number === String(value)
            )
          ) {
            return null;
          }

          return {
            country: k === 'rest' ? undefined : countriesMap['DE'].id,
            account_type: accountTypesMap[k].id,
            account_number: +value,
            id,
          };
        }
      );

      try {
        const res = await postClientAccountNumbers(
          activeCompany.company_id,
          accountNumbers.filter((v): v is NonNullable<typeof v> => v != null)
        );

        querClient.setQueryData(['clientAccountNumbers', queryKey], res);

        const changelogsRes = await getChangelogs(
          activeCompany?.company_id || '',
          'account_number',
          revenueAccsIds
        );

        querClient.setQueryData(
          ['RevenueAccsChangeLogs', queryKey],
          changelogsRes
        );

        setIsEditing(false);
        showNotification({
          text: 'Account numbers are updated successfully!',
          type: 'success',
        });
      } catch (error) {
        console.error(error);
      }
    },
  });

  const filteredDefaultAccounts = useMemo(
    () =>
      defaultAccountNumbers?.filter(
        (a) =>
          a.account_type in formik.values &&
          (a.account_type === 'rest' || a.country?.short_name === 'DE')
      ),
    // eslint-disable-next-line
    [defaultAccountNumbers]
  );

  useEffect(() => {
    if (clientAccountNumbers && filteredDefaultAccounts) {
      const values = JSON.parse(JSON.stringify(formik.values));
      filteredDefaultAccounts.forEach((a) => {
        values[a.account_type] = String(a.account_number);
      });

      const accs = clientAccountNumbers
        .filter(
          (a) =>
            a.account_type.account_type === 'rest' ||
            (a.account_type.account_type in formik.values &&
              (a.account_type.account_type === 'rest' ||
                a.country?.short_name === 'DE'))
        )
        .reduce(
          (acc, a) => {
            acc[a.account_type.account_type] = a;
            values[a.account_type.account_type] = String(a.account_number);
            return acc;
          },
          {} as Record<string, AccountNumber>
        );

      setAccountsMap(accs);
      formik.setValues(values);
      setInitialState(values);
    }
    // eslint-disable-next-line
  }, [clientAccountNumbers, filteredDefaultAccounts]);

  const normalizedDefaultAccsMap = useMemo(() => {
    return normalizeAccounts(filteredDefaultAccounts);
  }, [filteredDefaultAccounts]);

  if (isLoadingRevenueAccsChangeLogs) return <Loader />;

  return (
    <PageLayout
      className='flex-grow !bg-white !pt-0'
      rightColumn={
        <div className='max-w-[248px]'>
          <COARevenueAccountsInfoBlock />
        </div>
      }
    >
      <div>
        <COATabHeader
          title='Revenue Accounts Germany'
          onClickCancel={() => {
            formik.setValues(initialState);
            setIsEditing(false);
          }}
          onClickUpdate={() => formik.submitForm()}
          onClickEdit={() => setIsEditing(true)}
          isLoading={formik.isSubmitting}
          isEditing={isEditing}
          hasCustomAccountNumbers={
            !!(clientAccountNumbers || []).filter(
              (a) =>
                a.account_type.account_type in formik.values &&
                (a.account_type.account_type === 'rest' ||
                  a.country?.short_name === 'DE')
            ).length
          }
        >
          {!!revenueAccsChangeLogs?.length && (
            <div className='text-sm text-gray-700'>
              {t('Last updated')}:{' '}
              <COALastUpdated
                date={revenueAccsChangeLogs[0].created_at}
                role={revenueAccsChangeLogs[0].role_name}
              />
            </div>
          )}
        </COATabHeader>
        <div className='mb-2 rounded-lg border p-4 pt-2'>
          <div className='pb-4 text-h4 font-bold'>Umsatzsteuer</div>
          <div className='flex items-center justify-between gap-x-2'>
            <div className='pb-4 font-semibold'>
              {t('Germany Warehouse Location, VAT')} 19%
            </div>
            <div>
              <COACell
                value={formik.values.vat_rate_high}
                isEditing={isEditing}
                error={formik.errors.vat_rate_high || ''}
                touched={formik.touched.vat_rate_high || false}
                testId='vat_rate_high'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='vat_rate_high'
                isCustom={isCustom(
                  formik.values.vat_rate_high,
                  normalizedDefaultAccsMap.vat_rate_high
                )}
                inputWrapperClassName={`min-h-[66px] flex items-center`}
              />
            </div>
          </div>
          <div className='flex items-center justify-between gap-x-2'>
            <div className='pb-4 font-semibold'>
              {t('Germany Warehouse Location, VAT')} 7%
            </div>
            <div>
              <COACell
                value={formik.values.vat_rate_low}
                isEditing={isEditing}
                error={formik.errors.vat_rate_low || ''}
                touched={formik.touched.vat_rate_low || false}
                testId='vat_rate_low'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='vat_rate_low'
                isCustom={isCustom(
                  formik.values.vat_rate_low,
                  normalizedDefaultAccsMap.vat_rate_low
                )}
                inputWrapperClassName={`min-h-[66px] flex items-center`}
              />
            </div>
          </div>
          <div className='flex items-center justify-between gap-x-2'>
            <div className='pb-4 font-semibold'>
              {t('Germany Warehouse Location, VAT')} 0%
            </div>
            <div>
              <COACell
                value={formik.values.rest}
                isEditing={isEditing}
                error={formik.errors.rest || ''}
                touched={formik.touched.rest || false}
                testId='rest'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='rest'
                isCustom={isCustom(
                  formik.values.rest,
                  normalizedDefaultAccsMap.rest
                )}
                inputWrapperClassName={`min-h-[66px] flex items-center`}
              />
            </div>
          </div>
        </div>
        <div className='mb-4 rounded-lg border p-4 pt-2'>
          <div className='pb-4 text-h4 font-bold'>Steuerfreie Lieferungen</div>
          <div className='flex items-center justify-between gap-x-2'>
            <div className='pb-4 font-semibold'>
              {t('Intra-Community supply from Germany')}
            </div>
            <div>
              <COACell
                value={formik.values.b2b_buyer_vat_number_not_null}
                isEditing={isEditing}
                error={formik.errors.b2b_buyer_vat_number_not_null || ''}
                touched={formik.touched.b2b_buyer_vat_number_not_null || false}
                testId='b2b_buyer_vat_number_not_null'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='b2b_buyer_vat_number_not_null'
                isCustom={isCustom(
                  formik.values.b2b_buyer_vat_number_not_null,
                  normalizedDefaultAccsMap.b2b_buyer_vat_number_not_null
                )}
                inputWrapperClassName={`min-h-[66px] flex items-center`}
              />
            </div>
          </div>
          <div className='flex items-center justify-between gap-x-2'>
            <div className='pb-4 font-semibold'>
              {t('Export deliveries from Germany (tax-exempt)')}
            </div>
            <div>
              <COACell
                value={formik.values.arrival_country_not_EU}
                isEditing={isEditing}
                error={formik.errors.arrival_country_not_EU || ''}
                touched={formik.touched.arrival_country_not_EU || false}
                testId='arrival_country_not_EU'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='arrival_country_not_EU'
                isCustom={isCustom(
                  formik.values.arrival_country_not_EU,
                  normalizedDefaultAccsMap.arrival_country_not_EU
                )}
                inputWrapperClassName={`min-h-[66px] flex items-center`}
              />
            </div>
          </div>
        </div>
        <div className='mb-4 rounded-lg border p-4 pt-2'>
          <div className='pb-4 text-h4 font-bold'>Weitere Konten</div>
          <div className='flex items-center justify-between gap-x-2'>
            <div className='pb-4 font-semibold'>Commingling buy</div>
            <div>
              <COACell
                value={formik.values.commingling_buy_taxable}
                isEditing={isEditing}
                error={formik.errors.commingling_buy_taxable || ''}
                touched={formik.touched.commingling_buy_taxable || false}
                testId='commingling_buy_taxable'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='commingling_buy_taxable'
                isCustom={isCustom(
                  formik.values.commingling_buy_taxable,
                  normalizedDefaultAccsMap.commingling_buy_taxable
                )}
                inputWrapperClassName={`min-h-[66px] flex items-center`}
              />
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const normalizeAccounts = (accounts: DefaultAccWithType[] | undefined) => {
  return (accounts || []).reduce(
    (acc, a) => {
      acc[a.account_type] = String(a.account_number);
      return acc;
    },
    {} as Record<string, string>
  );
};
